/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { isScrolledIntoView } from "../utils/Dom";
import _ from "lodash";
import { Box, Spinner } from "@chakra-ui/react";
import { ChakraFAIcon } from "@/components/ui/Icon";

const BACKSPACE_KEY_CODE = 8;
const ENTER_KEY_CODE = 13;

class NuSingleSelect extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			selectedItem: this.findSelectedItem(this.props.selectedItem),
			filteredItems: [],
			filter: "",
			openSelection: false,
			focus: false,
			lastPressedKeyCode: null,
			updatedSearchPlaceHolder: null,
			cbLoading: false,
		};

		this.searchContainerRef = null;
		this.optionsListRef = null;
		this.inputRef = null;
		this.searchIconRef = null;
		this.arrowIconRef = null;
		this.hiddenFocusDivRef = null;

		// These keep track of which item is currently selected via keyboard navigation (up/down keys)
		this.currentOptionIndex = -1;
		this.currentOption = null;

		var debounceFunc = this.debounce(this.handleChange, 250);

		this.handleChangeBounced = (e) => {
			const newValue = e.target.value;
			this.setState({ filter: newValue });

			// Trim whitespace if trimWhitespace is enabled
			const trimmedValue = this.props.trimWhitespace ? newValue.trim() : newValue;

			// Only trigger onChange if minimum character requirement is met (after trimming if enabled)
			if (trimmedValue.length >= this.props.minChars) {
				debounceFunc(e);
			}
		};
	}

	findSelectedItem = (selectedValue) => {
		let selectedItem = undefined;
		if (selectedValue !== undefined) {
			for (const item of this.props.allItems) {
				if (item["value"] === selectedValue) {
					selectedItem = item;
				}
			}
		}
		return selectedItem;
	};

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	UNSAFE_componentWillUpdate(nextProps, nextState) {
		if (this.props.selectedItem !== nextProps.selectedItem) {
			this.setState({
				selectedItem: this.findSelectedItem(nextProps.selectedItem),
			});
		}
	}

	componentDidMount() {
		document.addEventListener("mousedown", this.handleClickOutside, { capture: true });
		window.addEventListener("blur", this.handleWindowBlur);
	}

	componentWillUnmount() {
		document.removeEventListener("mousedown", this.handleClickOutside, { capture: true });
		window.removeEventListener("blur", this.handleWindowBlur);
	}

	setSearchContainerRef = (node) => {
		this.searchContainerRef = node;
		if (this.props.searchContainerRef) this.props.searchContainerRef(node);
	};

	setInputRef = (node) => {
		// NOTE: Listener must be set on node to stop event propagating to document listener.
		//       Setting it using react onKeyUp prop, will not allow stopping propagation to document listener (even with the stopPropagation call)
		//       See https://github.com/facebook/react/issues/12518 (closed, but not yet resolved)
		if (node) {
			node.addEventListener("keyup", this.handleInputEnterKeyUp);
		} else {
			this.inputRef.removeEventListener("keyup", this.handleInputEnterKeyUp);
		}
		this.inputRef = node;
	};

	setOptionsListRef = (node) => {
		this.currentOptionIndex = -1;
		this.currentOption = null;
		this.optionsListRef = node;
	};

	// this function will hide the modal if the user clicks on a different tab or a window.
	handleWindowBlur = () => {
		this.setState({
			openSelection: false,
			filter: "",
			filteredItems: [],
		});

		this.hiddenFocusDivRef.focus();
	};

	handleClickOutside = (event) => {
		if (!this.searchContainerRef || !this.optionsListRef) return;

		if (!this.searchContainerRef.contains(event.target) && !this.optionsListRef.contains(event.target)) {
			event.stopPropagation();
			event.preventDefault();
			this.setState({
				openSelection: false,
				filter: "",
				filteredItems: [],
			});

			this.hiddenFocusDivRef.focus();
		}
	};

	handleSelect = async (item) => {
		this.setState({
			openSelection: false,
			filter: "",
			filteredItems: [],
			selectedItem: item,
			updatedSearchPlaceHolder: item["text"],
		});

		this.inputRef.blur();
		this.hiddenFocusDivRef.focus();

		this.props.onChange(item["value"], this.state.filter);
	};

	handleChange = async () => {
		if (this.currentOption) {
			this.currentOption.classList.remove("NuSelect_searchSelectItem--keyboard-focused");
		}

		let filterStr = this.state.filter;

		/* Apply any transformations only if the lastkey pressed is not backspace.
            For example, auto-insert a colon when inputting time.*/
		if (this.props.filterTransformCb && this.state.lastPressedKeyCode !== BACKSPACE_KEY_CODE) {
			filterStr = this.props.filterTransformCb(filterStr);
		}

		const filteredItems = await this.prepareFilteredItems(filterStr);

		this.setState({
			openSelection: filteredItems.length !== 0, // Close the selection dropdown if nothing matches the filter, allows parent component to show error msg
			filteredItems: this.props.showAllSelectItems ? this.props.allItems : filteredItems,
		});

		// If there is exactly one match, it means its a valid selection
		if (this.props.autoSelectWhenFilteredLengthIsOne && filteredItems.length === 1) {
			this.handleSelect(filteredItems[0]);
		}

		// Provision to allow parent component to display validation error msgs as-you-type
		if (this.props.filterMatchCb) {
			this.props.filterMatchCb(filteredItems.length);
		}
	};

	handleFocus = async () => {
		this.setState({
			filter: "",
			openSelection: true,
			filteredItems: await this.prepareFilteredItems(""),
		});
	};

	handleClick = async () => {
		this.inputRef.focus();
		this.setState({
			filter: "",
			openSelection: true,
			filteredItems: await this.prepareFilteredItems(""),
		});
	};

	handleInputKeyDown = (event) => {
		if (event.keyCode === 27) {
			// Esc
			event.preventDefault();
			event.stopPropagation();
			this.handleInputEscKey();
		} else if (event.keyCode === 9) {
			// Tab
			if (event.shiftKey) {
				// Shift+Tab
				this.setState({
					openSelection: false,
					filter: "",
					filteredItems: [],
				});
			} else {
				this.handleInputEscKey();
			}
		} else if (event.keyCode === 40) {
			// Down
			event.preventDefault();
			event.stopPropagation();

			if (event.repeat) {
				this.throttledInputDownKey();
			} else {
				this.handleInputDownKey();
			}
		} else if (event.keyCode === 38) {
			// Up
			event.preventDefault();
			event.stopPropagation();

			if (event.repeat) {
				this.throttledInputUpKey();
			} else {
				this.handleInputUpKey();
			}
		}
		this.setState({ lastPressedKeyCode: event.keyCode });
	};

	handleInputEscKey = () => {
		this.setState({
			openSelection: false,
			filter: "",
			filteredItems: [],
		});
		this.hiddenFocusDivRef.focus();
	};

	handleInputDownKey = () => {
		const options = this.optionsListRef.children;
		if (options.length === 0 || this.currentOptionIndex === options.length - 1) return;

		if (this.currentOption) {
			this.currentOption.classList.remove("NuSelect_searchSelectItem--keyboard-focused");
		}
		this.currentOption = options.item(++this.currentOptionIndex);
		this.currentOption.classList.add("NuSelect_searchSelectItem--keyboard-focused");

		if (!isScrolledIntoView(this.optionsListRef, this.currentOption)) {
			this.currentOption.scrollIntoView(false);
		}
	};
	throttledInputDownKey = _.throttle(this.handleInputDownKey, 175, {
		trailing: false,
	});

	handleInputUpKey = () => {
		if (!this.currentOption || this.currentOptionIndex === 0) return;

		this.currentOption.classList.remove("NuSelect_searchSelectItem--keyboard-focused");

		const options = this.optionsListRef.children;
		this.currentOption = options.item(--this.currentOptionIndex);
		this.currentOption.classList.add("NuSelect_searchSelectItem--keyboard-focused");

		if (!isScrolledIntoView(this.optionsListRef, this.currentOption)) {
			this.currentOption.scrollIntoView(true);
		}
	};
	throttledInputUpKey = _.throttle(this.handleInputUpKey, 175, {
		trailing: false,
	});

	handleInputEnterKeyUp = (event) => {
		event.preventDefault();
		event.stopPropagation();

		if (event.keyCode === ENTER_KEY_CODE && this.currentOption) {
			const item = this.state.filteredItems[this.currentOptionIndex];
			this.handleSelect(item);
		}

		if (event.keyCode === ENTER_KEY_CODE && this.state.filteredItems.length === 1) {
			const item = this.state.filteredItems[0];
			this.handleSelect(item);
		}
	};

	findValue = () => {
		if (this.state.filter) {
			return this.state.filter;
		} else if (this.state.selectedItem && !this.state.openSelection) {
			return this.state.selectedItem["text"];
		}
		return "";
	};

	prepareFilteredItems = async (filter) => {
		// reset the keyboard focussed item tracking
		this.currentOptionIndex = -1;
		if (this.currentOption) {
			this.currentOption.classList.remove("NuSelect_searchSelectItem--keyboard-focused");
		}
		this.currentOption = null;

		//If there is a callback for allItems, call that now
		let allItems = this.props.allItems;
		if (this.props.allItemsCb) {
			this.setState({ cbLoading: true });
			allItems = await this.props.allItemsCb(filter);
			this.setState({ cbLoading: false });
		}

		const filteredItems = [];
		for (const item of allItems) {
			if (item.dynamic) {
				const transformedValue = item.transformFilter(filter);
				if (item.shouldShow(transformedValue)) {
					filteredItems.push({
						text: item.renderText(transformedValue),
						value: "website",
						ok: item.isOk(transformedValue),
					});
				}
			} else {
				if (item.type === "section") {
					filteredItems.push(item);
					continue;
				}

				if (filter.length <= 2 && item["text"].toLowerCase().startsWith(filter.toLowerCase())) {
					filteredItems.push(item);
				} else if (filter.length > 2 && item["text"].toLowerCase().includes(filter.toLowerCase())) {
					filteredItems.push(item);
				} else if (filter.length === 0) {
					filteredItems.push(item);
				}
			}
		}

		// remove sections with no results
		for (let i = filteredItems.length - 1; i >= 0; i--) {
			const item = filteredItems[i];
			if (item.type === "section") {
				if (i === filteredItems.length - 1) {
					// section is last item - nothing follows it
					filteredItems.splice(i, 1);
				} else {
					const nextItem = filteredItems[i + 1];
					if (nextItem.type === "section" || nextItem.value === "website") {
						// section is followed by another section or a website entry - meaning no items in this section
						filteredItems.splice(i, 1);
					}
				}
			}
		}

		return this.props.noLimitSelectItems ? filteredItems : filteredItems.slice(0, 100);
	};

	getDropdownWidth = () => {
		if (this.searchContainerRef === undefined) {
			return 0;
		}

		return this.searchContainerRef.getBoundingClientRect().width;
	};

	setFocussedClass = () => {
		if (this.searchIconRef) {
			this.searchIconRef.classList.add("NuSelect_searchFieldFocused--no-opacity");
		}
		this.arrowIconRef.classList.add("NuSelect_searchFieldFocused--no-opacity");
	};

	removeFocussedClass = () => {
		if (this.searchIconRef) {
			this.searchIconRef.classList.remove("NuSelect_searchFieldFocused--no-opacity");
		}
		this.arrowIconRef.classList.remove("NuSelect_searchFieldFocused--no-opacity");
	};

	renderItems = () => {
		const preparedItems = [];
		for (const item of this.state.filteredItems) {
			preparedItems.push(
				item.type === "section" ? (
					<div key={item.value || item.text} className="NuSelect_searchSelectSectionItem">
						{item.text}
					</div>
				) : (
					<div key={item.value || item.text} className="NuSelect_searchSelectItem" onClick={() => this.handleSelect(item)}>
						<span>{item.text}</span>
						{item.icons ? (
							<span className="NuSelect_searchSelectItemIcons">
								{item.icons.map((icon) => (
									<img key={`${item.value}-icon`} style={{ display: "inline" }} src={icon} alt="device-platform" />
								))}
							</span>
						) : null}
					</div>
				)
			);
		}
		if (this.state.filteredItems.length > 99 && !this.props.noLimitSelectItems) {
			preparedItems.push(
				<div key="truncated_items_info" className="NuSelect_searchSelectItemTruncateInfo">
					Too many values to show them all. Please use search to narrow down results.
				</div>
			);
		}
		return preparedItems;
	};

	renderSelection = () => {
		if (this.state.openSelection) {
			return (
				<div
					ref={this.setOptionsListRef}
					className="NuSelect_searchSelectList"
					style={{ width: "" + this.getDropdownWidth() + "px" }}
					data-testid="NuSelect_searchSelectList"
				>
					{this.renderItems()}
				</div>
			);
		}
	};

	/** This function provides ability to have different labels to the input box when
        select dropdown is open and when it is closed. */
	getPlaceHolder = () => {
		if (this.state.updatedSearchPlaceHolder) {
			return this.state.updatedSearchPlaceHolder;
		} else if (this.state.openSelection && this.props.openSelectionPlaceholder) {
			return this.props.openSelectionPlaceholder;
		} else {
			return this.props.searchPlaceholder;
		}
	};

	debounce = (func, wait) => {
		let timeout;

		return function executedFunction(event) {
			event.persist();
			const later = () => {
				clearTimeout(timeout);
				func(event);
			};

			clearTimeout(timeout);
			timeout = setTimeout(later, wait);
		};
	};

	render() {
		return (
			<div>
				{this.props.title && <div className="NuSelect_selectedTitle">{this.props.title}</div>}
				<div
					className={clsx(this.state.openSelection && "NuSelect_searchOpen", "nuselect_search", this.props.className)}
					onFocus={this.setFocussedClass}
					onBlur={this.removeFocussedClass}
				>
					<div
						ref={this.setSearchContainerRef}
						className={clsx(
							"NuSelect_searchField",
							this.props.invalid && "invalid",
							this.props.loading && "NuSelect_searchField--disabled"
						)}
					>
						{!this.props.noSearchIcon && (
							<ChakraFAIcon
								ref={(node) => (this.searchIconRef = node)}
								icon="fa-search"
								variant="solid"
								className="NuSelect_searchFieldSearchIcon"
							/>
						)}
						<input
							className={clsx("NuSelect_searchFieldInput", !this.state.openSelection && !this.props.loading && "clickable")}
							ref={this.setInputRef}
							type="text"
							value={this.findValue()}
							onChange={this.handleChangeBounced}
							placeholder={this.props.loading ? "Loading..." : this.getPlaceHolder()}
							onKeyDown={this.handleInputKeyDown}
							// keyUp used as the modal uses keyUp for it's enter handler, we need to stop propagation to the modal handler
							onKeyUp={this.handleInputEnterKeyUp}
							onFocus={this.handleFocus}
							disabled={this.props.loading}
							readOnly={this.props.readOnly}
						/>
						<Box onClick={this.handleClick}>
							{this.state.cbLoading ? (
								<Spinner ref={(node) => (this.arrowIconRef = node)} />
							) : (
								<ChakraFAIcon
									ref={(node) => (this.arrowIconRef = node)}
									icon="fa-angle-down"
									variant="solid"
									className="NuSelect_searchFieldArrowIcon"
								/>
							)}
						</Box>
					</div>
					{this.renderSelection()}
				</div>
				{/* focus is set here after a selection is made, allows fluent tab navigation back to select, or forward to next tabbable element */}
				<div tabIndex={-1} ref={(node) => (this.hiddenFocusDivRef = node)} className="NuSelect_selectFocusDiv" />
			</div>
		);
	}
}

NuSingleSelect.propTypes = {
	readOnly: PropTypes.bool,
	loading: PropTypes.bool,
	noSearchIcon: PropTypes.bool,
	invalid: PropTypes.bool,
	title: PropTypes.string,
	className: PropTypes.string,
	searchPlaceholder: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
	openSelectionPlaceholder: PropTypes.string,
	noLimitSelectItems: PropTypes.bool,
	allItems: PropTypes.arrayOf(PropTypes.object),
	allItemsCb: PropTypes.func,
	filterMatchCb: PropTypes.func,
	autoSelectWhenFilteredLengthIsOne: PropTypes.bool,
	showAllSelectItems: PropTypes.bool,
	filterTransformCb: PropTypes.func,
	onChange: PropTypes.func,
	selectedItem: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	searchContainerRef: PropTypes.func,
	minChars: PropTypes.number,
	trimWhitespace: PropTypes.bool,
};

NuSingleSelect.defaultProps = {
	allItems: [],
	loading: false,
	readOnly: false,
	minChars: 0,
	trimWhitespace: false,
};

export default NuSingleSelect;
